import type { GpsTriggerItem } from "./GpsTriggerContextParser";

export type GpsTriggerMediaOutcome =
    | "cancelled"
    | "same-active"
    | "different-active"
    | "destination-banner"
    | "tour-autoplayed"
    | "tour-autoplay-blocked"
    | "tour-no-story";

export type GpsTriggerPlayerAdapter = {
    readonly activeMediaId: string | null;
    readonly activeMediaRevision: number;
    readonly isPlaying: boolean;
    pause: () => void;
    resume: () => Promise<void>;
    prepareItem: (item: GpsTriggerItem) => void;
    playItem: (item: GpsTriggerItem) => Promise<void>;
};

export class GpsTriggerMediaCoordinator {
    constructor(
        private readonly player: GpsTriggerPlayerAdapter,
        private readonly playCue: () => Promise<void>,
        private readonly vibrate: (durationMs: number) => void
    ) {
    }

    async handle(
        mode: "destination" | "tour",
        item: GpsTriggerItem,
        isCurrent: () => boolean = () => true,
        allowTourAutoplay = true
    ): Promise<GpsTriggerMediaOutcome> {
        const activeMediaId = this.player.activeMediaId;
        const activeMediaRevision = this.player.activeMediaRevision;
        if (mode === "tour" && activeMediaId && item.audio?.id && activeMediaId === item.audio.id) {
            return "same-active";
        }

        if (activeMediaId) {
            const resumeAfterCue = this.player.isPlaying;
            if (resumeAfterCue) {
                this.player.pause();
            }
            try {
                await this.cue();
            } finally {
                if (resumeAfterCue && isCurrent() && this.player.activeMediaId === activeMediaId && this.player.activeMediaRevision === activeMediaRevision) {
                    try {
                        await this.player.resume();
                    } catch {
                        // The original item remains selected when browser playback cannot resume.
                    }
                }
            }
            return mode === "destination" ? "destination-banner" : "different-active";
        }

        await this.cue();
        if (!isCurrent()) {
            return "cancelled";
        }

        if (mode === "destination") {
            return "destination-banner";
        }
        if (!item.audio) {
            return "tour-no-story";
        }
        if (!allowTourAutoplay) {
            this.player.prepareItem(item);
            return "tour-autoplay-blocked";
        }

        try {
            await this.player.playItem(item);
            return "tour-autoplayed";
        } catch {
            return "tour-autoplay-blocked";
        }
    }

    private async cue(): Promise<void> {
        try {
            this.vibrate(500);
        } catch {
            // Vibration is optional and must not block the cue or trigger action.
        }

        try {
            await this.playCue();
        } catch {
            // Browsers may block or fail the short cue; continue with the product action.
        }
    }
}
